import { useLiveQuery } from 'dexie-react-hooks';
import type { ComponentProps } from 'react';
import type { ExpenseCategoryRecord } from '@dairy/shared';
import { useSession } from '@/auth/useSession';
import { Select } from '@/components/ui';
import { db } from '@/db/db';

export function useCategories(): ExpenseCategoryRecord[] {
  return useLiveQuery(() => db.expenseCategories.toArray(), []) ?? [];
}

export function categoryName(c: ExpenseCategoryRecord | undefined, locale: string): string {
  if (!c) return '?';
  return locale === 'am' ? c.nameAm : c.name;
}

/** EX-2 two-level picker (group › category), names in the user's language; only active leaves are selectable. */
export function CategorySelect({ value, onChange, includeInactiveId, ...props }: Omit<ComponentProps<'select'>, 'onChange'> & { value: string; onChange: (id: string) => void; includeInactiveId?: string }) {
  const locale = useSession().profile?.locale ?? 'am';
  const cats = useCategories();
  const groups = cats.filter((c) => !c.parentId).sort((a, b) => a.sortOrder - b.sortOrder);
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} {...props}>
      <option value="" />
      {groups.map((g) => (
        <optgroup key={g.id} label={categoryName(g, locale)}>
          {cats
            .filter((c) => c.parentId === g.id && (c.active || c.id === includeInactiveId))
            .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
            .map((c) => (
              <option key={c.id} value={c.id}>
                {categoryName(c, locale)}
              </option>
            ))}
        </optgroup>
      ))}
    </Select>
  );
}
